// frontEnd/src/Component/TransactionDetail/TransactionDetail.js
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import "./TransactionDetail.css";
const REACT_APP_EXPRESS_API = process.env.REACT_APP_EXPRESS_API;
/**
 * Pakai ENV:
 * - REACT_APP_API_URL = http://localhost:5001
 * - fallback ke localhost bila ENV tidak di-set
 * - otomatis menambahkan /api/transactions/linechart tanpa double slash
 */
const BASE_URL =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_API_URL) ||
  `${REACT_APP_EXPRESS_API}`;
const LINE_API = `${String(BASE_URL).replace(/\/$/, "")}/api/transactions/linechart`;

/* ===== Util ===== */
const idr = (n) =>
  `Rp${Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;

const toDate = (v) => {
  const d = new Date(v);
  return isNaN(d) ? null : d;
};
const dateKey = (d) => {
  const dd = new Date(d);
  const y = dd.getFullYear();
  const m = String(dd.getMonth() + 1).padStart(2, "0");
  const day = String(dd.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/* >>> Ganti formatter tanggal di chart & tooltip <<< */
const ID_MONTHS = [
  "januari",
  "februari",
  "maret",
  "april",
  "mei",
  "juni",
  "juli",
  "agustus",
  "september",
  "oktober",
  "november",
  "desember",
];
const labelDate = (v) => {
  const d = toDate(v);
  if (!d) return v;
  const day = d.getDate(); // tanpa leading zero
  const monthName = ID_MONTHS[d.getMonth()]; // huruf kecil
  const year = d.getFullYear();
  return `${day} ${monthName} ${year}`;
};

const startFromFilter = (f) => {
  const now = new Date();
  const s = new Date(now);
  if (f === "day") s.setDate(now.getDate() - 1);
  else if (f === "week") s.setDate(now.getDate() - 7);
  else if (f === "month") s.setDate(now.getDate() - 30);
  else return null;
  s.setHours(0, 0, 0, 0);
  return s;
};

/* ===== Normalizer untuk /linechart ===== */
const pickList = (raw) =>
  Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.transactions)
        ? raw.transactions
        : [];

const normalizeLineItem = (row) => {
  if (!row || typeof row !== "object") return null;
  const date = row.date ?? row.tanggal ?? row.x ?? row.label;
  const total = row.totalNominal ?? row.nominal ?? row.y ?? row.value ?? 0;
  const d = toDate(date);
  if (!d) return null;
  return { x: dateKey(d), y: Number(total || 0) };
};
const normalizeLineList = (raw) =>
  pickList(raw).map(normalizeLineItem).filter(Boolean).sort((a, b) => (a.x < b.x ? -1 : 1));

/* ===== Tooltip: hanya Tanggal & Nominal ===== */
function OnlyXYTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0] || {};
  const val = p.value ?? 0;
  return (
    <div className="td-tip">
      <div className="td-tip-body">
        <div className="td-tip-row">
          <span className="td-tip-label">Tanggal</span>
          <span className="td-tip-value">{labelDate(label)}</span>
        </div>
        <div className="td-tip-row">
          <span className="td-tip-label">Nominal</span>
          <span className="td-tip-value">{idr(val)}</span>
        </div>
      </div>
    </div>
  );
}

export default function TransactionDetail() {
  const [range, setRange] = useState("month"); // default "Bulan"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await fetch(LINE_API, {
          method: "GET",
          credentials: "include", // kirim cookie ke server
          headers: { Accept: "application/json" },
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Gagal memuat grafik transaksi");

        setRows(normalizeLineList(data));
      } catch (e) {
        setErr(e?.message || "Terjadi kesalahan saat memuat data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const series = useMemo(() => {
    const start = startFromFilter(range);
    const filtered = start
      ? rows.filter((r) => {
        const d = toDate(r.x);
        return d && d >= start;
      })
      : rows;

    if (!filtered.length) {
      const demo = [0, 0, 0, 0, 0, 0, 0];
      const today = new Date();
      return demo.map((y, i) => ({
        x: dateKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() - (demo.length - i))),
        y,
      }));
    }
    return filtered;
  }, [rows, range]);

  const yMax = useMemo(() => {
    const m = Math.max(...series.map((p) => p.y || 0), 1);
    return Math.ceil(m * 1.25);
  }, [series]);

  return (
    <div className="td-card">
      <div className="td-header-row">
        <div className="td-filter-row">
          <button className={`td-chip ${range === "week" ? "active" : ""}`} onClick={() => setRange("week")}>
            Minggu
          </button>
          <button className={`td-chip ${range === "month" ? "active" : ""}`} onClick={() => setRange("month")}>
            Bulan
          </button>
        </div>
      </div>

      {loading && <div className="td-loading">Memuat data transaksi…</div>}
      {!loading && err && <div className="td-error">{err}</div>}

      <div className="td-chart-shell">
        <div className="td-chart-outline">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={series} margin={{ top: 8, right: 24, left: 24, bottom: 0 }}>
              {series.map((p, i) => (
                <ReferenceLine key={`v-${i}`} x={p.x} stroke="rgba(30, 58, 138, 0.08)" strokeWidth={1} />
              ))}

              <defs>
                <linearGradient id="tdGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E3A8A" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <XAxis dataKey="x" tickFormatter={labelDate} minTickGap={28} />
              <YAxis hide domain={[0, yMax]} />

              <Area
                type="monotone"
                dataKey="y"
                stroke="#1E3A8A"
                strokeWidth={3}
                fill="url(#tdGrad)"
                activeDot={{ r: 6 }}
                dot={{ r: 4 }}
              />
              <Tooltip
                cursor={{ stroke: "transparent" }}
                content={<OnlyXYTooltip />}
                wrapperStyle={{ outline: "none" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
